/**
 * La Situazione sul telefono (7/10/2026): risponde a "come stiamo con i soldi?".
 *
 * Prima, a 390px, era quasi uguale a Oggi: la stessa frase di sei righe, gli
 * stessi tre numeri in riquadri, poi le cose da fare dentro un'altra scheda.
 * Qui:
 *   1. in grande, i soldi sul conto oggi (si tocca e si va in Banca);
 *   2. i prossimi mesi, uno per riga, col saldo di fine mese: il mese sotto
 *      zero è rosso e si apre per vedere cosa entra e cosa esce. È la stessa
 *      cosa che diceva la frase, ma si legge in un secondo;
 *   3. cosa ci devono i clienti e cosa abbiamo di scaduto;
 *   4. le cose da fare dell'Amministrazione;
 *   5. le altre pagine dell'Amministrazione e da quando sono aggiornati i dati.
 * Stessa chiamata e stessa cache del computer (GET /admin/situazione).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { fmtEur } from '../../../lib/utils';
import { ADMIN_PAGES, ADMIN_PATH, canSeeAdminPage, type AdminPageKey } from '../../../lib/adminRoutes';
import { MobileSheet } from '../../../components/ui/MobileSheet';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow } from '../../../components/mobile/MobileKit';
import { useActionStream } from '../../oggi/useActionStream';
import { dividi } from '../../oggi/OggiMobile';
import { situazioneQuery, type MesePrevisione } from './situazioneApi';

const PAGINA_DELLO_SCADUTO: Record<string, AdminPageKey> = {
  fisco: 'fisco', fornitori: 'spese', speseFisse: 'spese', contanti: 'spese', stipendi: 'dipendenti',
};

const euro = (n: number) => `${n < 0 ? '−' : ''}€ ${fmtEur(Math.abs(n))}`;

export function SituazioneMobile() {
  const navigate = useNavigate();
  const user = useAuthStore((st) => st.user);
  const { data: s, isLoading } = useQuery({ ...situazioneQuery({ mesi: 3 }), placeholderData: keepPreviousData });
  const { bands } = useActionStream({ scope: 'admin' });
  const [mese, setMese] = useState<MesePrevisione | null>(null);
  const [tutte, setTutte] = useState(false);

  if (!s) {
    return (
      <MPage>
        <MEmpty>{isLoading ? 'Carico la situazione…' : "L'Amministrazione non risponde: riprova tra un momento."}</MEmpty>
      </MPage>
    );
  }

  const daFare = [...(bands.find((b) => b.band === 0)?.items ?? []), ...(bands.find((b) => b.band === 1)?.items ?? [])];
  const visibili = tutte ? daFare : daFare.slice(0, 4);
  let dove: AdminPageKey = PAGINA_DELLO_SCADUTO[s.scaduti.principale ?? 'fisco'] ?? 'fisco';
  if (dove === 'dipendenti' && !canSeeAdminPage(user, 'dipendenti')) dove = 'spese';
  const pagine = ADMIN_PAGES.filter((p) => p.key !== 'situazione' && !p.nascosta && canSeeAdminPage(user, p));
  const sotto = s.previsione.mesi.find((m) => m.saldoFine < 0);

  return (
    <MPage>
      <MHero
        label="Sul conto oggi"
        unit="€"
        value={fmtEur(s.disponibili.totale)}
        sub={
          <>
            {s.disponibili.bancaAggiornataTesto}
            {sotto && <> · <span className="is-alert">sotto zero a fine {sotto.nome.toLowerCase()}</span></>}
          </>
        }
        onClick={() => navigate(ADMIN_PATH.banca)}
      />

      <MGroup title="I prossimi mesi" note="Saldo previsto a fine mese, con quello che deve ancora scadere. Lo scaduto è a parte, qui sotto.">
        {s.previsione.mesi.map((m) => (
          <MRow
            key={m.mese}
            text={m.nome.slice(0, 3)}
            tone={m.saldoFine < 0 ? 'alert' : undefined}
            title={`Fine ${m.nome.toLowerCase()}`}
            // "+5.026 −11.042": entrate e uscite del mese in poco spazio (a 360px "entra …, esce …" si tagliava).
            sub={`+${fmtEur(m.entra.totale)}  −${fmtEur(m.esce.totale)}`}
            trail={euro(m.saldoFine)}
            trailTone={m.saldoFine < 0 ? 'alert' : undefined}
            onClick={() => setMese(m)}
          />
        ))}
      </MGroup>

      <MGroup title="Da incassare e da pagare">
        <MRow
          icon="invoice"
          title="Ci devono i clienti"
          sub={s.clienti.fattureInRitardo > 0
            ? <span className="is-alert">{s.clienti.fattureInRitardo} {s.clienti.fattureInRitardo === 1 ? 'fattura in ritardo' : 'fatture in ritardo'}</span>
            : `${s.clienti.fattureAperte} ${s.clienti.fattureAperte === 1 ? 'fattura aperta' : 'fatture aperte'}, nessuna in ritardo`}
          trail={`€ ${fmtEur(s.clienti.totale)}`}
          onClick={() => navigate(ADMIN_PATH.fatture)}
        />
        <MRow
          icon="calendarClock"
          tone={s.scaduti.totale > 0 ? 'alert' : undefined}
          title="Pagamenti scaduti"
          sub={s.scaduti.totale > 0 ? s.scaduti.descrizione : 'niente di scaduto'}
          subWrap
          trail={`€ ${fmtEur(s.scaduti.totale)}`}
          onClick={() => navigate(ADMIN_PATH[dove])}
        />
      </MGroup>

      <MGroup title="Da fare" count={daFare.length}>
        {daFare.length === 0 ? (
          <MEmpty>Niente da fare oggi per l'Amministrazione.</MEmpty>
        ) : (
          <>
            {visibili.map((it) => {
              const d = dividi(it);
              return (
                <MRow
                  key={it.key}
                  icon={it.icon}
                  tone={it.band === 0 ? 'alert' : undefined}
                  title={d.title}
                  sub={d.sub}
                  trail={it.amount != null ? `€ ${fmtEur(it.amount)}` : undefined}
                  onClick={() => navigate(it.entity.path)}
                />
              );
            })}
            {daFare.length > 4 && <MMore label={tutte ? 'Mostra meno' : `Vedi tutte le ${daFare.length}`} onClick={() => setTutte((v) => !v)} />}
          </>
        )}
      </MGroup>

      <MGroup title="Amministrazione">
        {pagine.map((p) => (
          <MRow key={p.key} icon={p.icon} title={p.label} sub={p.sub} onClick={() => navigate(p.to)} />
        ))}
      </MGroup>

      <MGroup title="Dati aggiornati">
        <MRow icon="landmark" tone={s.sync.qonto.problema ? 'alert' : undefined} title="Banca" sub={s.sync.qonto.testo} subWrap />
        <MRow icon="invoice" tone={s.sync.fic.problema ? 'alert' : undefined} title="Fatture in Cloud" sub={s.sync.fic.testo} subWrap />
      </MGroup>

      <MobileSheet open={!!mese} onClose={() => setMese(null)} title={mese ? `Fine ${mese.nome.toLowerCase()}: ${euro(mese.saldoFine)}` : undefined}>
        {mese && (
          <div className="mk" style={{ gap: 'var(--s-4)', paddingBottom: 0 }}>
            <MGroup title="Entra" count={undefined}>
              {mese.entra.parti.map((pt) => (
                <MRow key={pt.chiave} title={pt.etichetta} sub={pt.voci.slice(0, 3).map((v) => v.nome).join(', ') || undefined} trail={`€ ${fmtEur(pt.importo)}`} trailTone={pt.importo === 0 ? 'mute' : undefined} />
              ))}
            </MGroup>
            <MGroup title="Esce">
              {mese.esce.parti.map((pt) => (
                <MRow key={pt.chiave} title={pt.etichetta} sub={pt.voci.slice(0, 3).map((v) => v.nome).join(', ') || undefined} trail={`€ ${fmtEur(pt.importo)}`} trailTone={pt.importo === 0 ? 'mute' : undefined} />
              ))}
            </MGroup>
          </div>
        )}
      </MobileSheet>
    </MPage>
  );
}
